////// Lib. //////
//////////////////

import Component from "gia/Component";
import ProjectImageStream from "./project-image-stream.js";
import ProjectScroller from "./project-scroller.js";
import ImagePrefetcher from "../../../../app/modules/ImagePrefetcher.js";
import { applyTabState } from "./project-viewer-v2-tabs.js";
import { applySort, syncSortHeaders, getNextSortDirection } from "./project-viewer-v2-sort.js";
import { infoPanelFor, setInfoPanelOpen, setInfoPanelOpenForItem, resetInfoPanel, setActiveItem, setupInfoScrollIndicators } from "./project-viewer-v2-info.js";

const MOBILE_MEDIA_QUERY = "(max-width: 767px), (max-height: 767px) and (pointer: coarse)";

// Comp. class ///
//////////////////

export default class ProjectViewerV2 extends Component {
  /////////// Constructor ////////////
  ////////////////////////////////////

  constructor(el, options) {
    super(el);

    this.ref = {
      btns_tab: [],
      panel: [],
      sort: [],
      item: [],
      zoom: [],
      playPause: [],
      info: [],
    };

    this.options = {
      name: "ProjectViewerV2",
      zoomStep: 10, // multiple of --unit-spacing added to the image stream's height per zoom click
      scrollGapUnits: 1, // multiple of --unit-spacing kept between the tab bar and an opened row
      scrollDuration: 0.7,
      lockScroll: false,
      prefetch: true,
      prefetchConcurrency: 2,
      ...options,
    };

    this._onTabClick = null;
    this._onTabKeyDown = null;
    this._onSortClick = null;
    this._onItemClick = null;
    this._onItemKeyDown = null;
    this._onZoomClick = null;
    this._onStreamClick = null;
    this._streams = [];
    this._onPlayPauseClick = null;
    this._onInfoClick = null;
    this._onKeyDown = null;
    this._onScroll = null;
    this._stream = null;
    this._scroller = null;
    this._prefetcher = null;
    this._destroyInfoScrollIndicators = null;
    this._activeItem = null;
    this._sortState = new Map();
    this._isMobile = false;
  }

  ///////////// Mount ////////////////
  ////////////////////////////////////

  mount() {
    if (!this.ref.btns_tab.length || !this.ref.panel.length) return;

    this._isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;

    this._onTabClick = (e) => {
      this._activateTab(this.ref.btns_tab.indexOf(e.currentTarget));
    };

    this._onTabKeyDown = (e) => {
      const currentIndex = this.ref.btns_tab.indexOf(e.currentTarget);
      if (currentIndex === -1) return;

      let nextIndex = null;
      if (e.key === "ArrowRight") nextIndex = (currentIndex + 1) % this.ref.btns_tab.length;
      if (e.key === "ArrowLeft") nextIndex = (currentIndex - 1 + this.ref.btns_tab.length) % this.ref.btns_tab.length;
      if (e.key === "Home") nextIndex = 0;
      if (e.key === "End") nextIndex = this.ref.btns_tab.length - 1;
      if (nextIndex === null) return;

      e.preventDefault();
      this.ref.btns_tab[nextIndex].focus();
      this._activateTab(nextIndex);
    };

    this.ref.btns_tab.forEach((tab, i) => {
      tab.addEventListener("click", this._onTabClick);
      tab.addEventListener("keydown", this._onTabKeyDown);
      tab.setAttribute("tabindex", i === 0 ? "0" : "-1");
    });

    this._onSortClick = (e) => {
      const header = e.currentTarget;
      const key = header.dataset.sortKey;
      const panel = this._activePanel();

      if (!panel || !key) return;
      this._sortPanel(panel, key, header);
    };

    this.ref.sort.forEach((header) => {
      header.addEventListener("click", this._onSortClick);
    });

    this._onItemClick = (e) => {
      this._toggleItem(e.currentTarget);
    };

    this._onItemKeyDown = (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      this._toggleItem(e.currentTarget);
    };

    this.ref.item.forEach((it) => {
      it.addEventListener("click", this._onItemClick);
      it.addEventListener("keydown", this._onItemKeyDown);
    });

    this._onZoomClick = (e) => {
      this._toggleZoom(e.currentTarget.closest(".project-viewer-v2__item-panel"));
    };

    this.ref.zoom.forEach((btn) => {
      btn.addEventListener("click", this._onZoomClick);
    });

    // The stream itself toggles zoom too. Bound on the images container rather
    // than the panel, so the play/pause, zoom and info buttons beside it keep
    // their own click behaviour.
    this._onStreamClick = (e) => {
      this._toggleZoom(e.currentTarget.closest(".project-viewer-v2__item-panel"));
    };

    this._streams = Array.from(this.element.querySelectorAll(".project-viewer-v2__item-images"));
    // On mobile the stream is a slideshow (drag/tap belongs to Splide), so the
    // click-to-zoom behaviour is desktop-only.
    if (!this._isMobile) {
      this._streams.forEach((stream) => {
        stream.addEventListener("click", this._onStreamClick);
        stream.classList.add("cursor-zoom-in");
      });
    }

    this._onPlayPauseClick = (e) => {
      const btn = e.currentTarget;
      const panel = btn.closest(".project-viewer-v2__item-panel");
      const isPlaying = this._stream?.togglePlayback(panel);
      if (isPlaying === null || isPlaying === undefined) return;

      btn.dataset.playing = isPlaying ? "true" : "false";
      btn.setAttribute("aria-label", isPlaying ? "Pause auto-scroll" : "Resume auto-scroll");
    };

    this.ref.playPause.forEach((btn) => {
      btn.addEventListener("click", this._onPlayPauseClick);
    });

    this._onInfoClick = (e) => {
      const imagePanel = e.currentTarget.closest(".project-viewer-v2__item-panel");
      const item = imagePanel?.previousElementSibling;
      if (!item) return;

      // Info and zoom are mutually exclusive: revealing info while zoomed un-zooms first.
      const zoomBtn = imagePanel?.querySelector('[g-ref="zoom"]');
      if (zoomBtn?.dataset.zoomed === "true") {
        this._toggleZoom(imagePanel);
        this._setInfoPanelOpenForItem(item, true);
        return;
      }

      this._toggleInfoPanel(item);
    };

    this.ref.info.forEach((btn) => {
      btn.addEventListener("click", this._onInfoClick);
    });

    this._onKeyDown = (e) => {
      if (e.key === "Escape" && this._activeItem) this._closeItem(this._activeItem);
    };

    window.addEventListener("keydown", this._onKeyDown);
    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      this._onScroll = () => this._syncMobileLogo();
      window.addEventListener("scroll", this._onScroll, { passive: true });
      this._syncMobileLogo();
    }

    this._stream = new ProjectImageStream({ zoomStep: this.options.zoomStep });
    this._destroyInfoScrollIndicators = setupInfoScrollIndicators(this.element);
    this._scroller = new ProjectScroller({
      offsetEl: this.element.querySelector('[role="tablist"]'),
      gapUnits: this.options.scrollGapUnits,
      duration: this.options.scrollDuration,
      lockScroll: this.options.lockScroll,
      onScrollStart: () => this._setInteractionsBlocked(true),
      onScrollEnd: () => this._setInteractionsBlocked(false),
    });

    // Read before _activateTab(), which resets every header's data-active via
    // syncSortHeaders() as soon as it runs (no sort state exists for any panel yet).
    const initialHeader = this.ref.sort.find((header) => header.dataset.active === "true") ?? this.ref.sort[0];
    const initialDirection = initialHeader?.getAttribute("aria-sort") === "descending" ? "descending" : "ascending";

    this._activateTab(0);

    const activePanel = this._activePanel();
    if (activePanel && initialHeader) {
      this._sortPanel(activePanel, initialHeader.dataset.sortKey, initialHeader, initialDirection);
    }

    // Deferred: _activateTab and _sortPanel above reorder the rows, and the scroll-into-view
    // needs their final positions.
    requestAnimationFrame(() => this._openFromHash());

    this._startPrefetch();
  }

  //////////// Unmount ///////////////
  ////////////////////////////////////

  unmount() {
    this.ref.btns_tab.forEach((tab) => {
      tab.removeEventListener("click", this._onTabClick);
      tab.removeEventListener("keydown", this._onTabKeyDown);
    });

    this.ref.sort.forEach((header) => {
      header.removeEventListener("click", this._onSortClick);
    });

    this.ref.item.forEach((item) => {
      item.removeEventListener("click", this._onItemClick);
      item.removeEventListener("keydown", this._onItemKeyDown);
    });

    this.ref.zoom.forEach((btn) => {
      btn.removeEventListener("click", this._onZoomClick);
    });

    this._streams.forEach((stream) => {
      stream.removeEventListener("click", this._onStreamClick);
    });
    this._streams = [];

    this.ref.playPause.forEach((btn) => {
      btn.removeEventListener("click", this._onPlayPauseClick);
    });

    this.ref.info.forEach((btn) => {
      btn.removeEventListener("click", this._onInfoClick);
    });

    window.removeEventListener("keydown", this._onKeyDown);
    if (this._onScroll) window.removeEventListener("scroll", this._onScroll);
    document.documentElement.classList.remove("has-project-viewer-scroll");
    this._stream?.destroy();
    this._scroller?.destroy();
    this._prefetcher?.destroy();
    this._destroyInfoScrollIndicators?.();
    this._setInteractionsBlocked(false);
  }

  /////////// Private ////////////////
  ////////////////////////////////////

  // Rows only build their images when opened, so nothing here is observable by
  // the lazy loader. Warming the cache in the background instead means a row
  // that gets opened later usually has its images ready already.
  _startPrefetch() {
    if (!this.options.prefetch) return;

    const sources = this.ref.item.map((item) => {
      try {
        return JSON.parse(item.dataset.images || "[]");
      } catch {
        return [];
      }
    });

    this._prefetcher = new ImagePrefetcher({ concurrency: this.options.prefetchConcurrency });

    // Placeholders first — they're a couple of KB each and are what's shown the
    // instant a row opens, so covering all of them removes the initial flash.
    this._prefetcher.add(sources.flat().map((image) => image.placeholder));

    // Then the full images, interleaved column-wise: every row gets its first
    // image before any row gets its second, so no single project hogs the queue.
    const longest = sources.reduce((max, images) => Math.max(max, images.length), 0);
    for (let i = 0; i < longest; i++) {
      this._prefetcher.add(sources.map((images) => images[i]?.src));
    }

    this._prefetcher.start();
  }

  // Entry point for the work stream's "view full project" links.
  _openFromHash() {
    const match = /^#project-(.+)$/.exec(window.location.hash);
    if (!match) return;

    const projectId = decodeURIComponent(match[1]);

    // Every project is rendered once per tab it belongs to, so the row inside a hidden
    // panel has to be skipped or the item would open out of view.
    const item = this.ref.item.find((it) => it.dataset.projectId === projectId && !it.closest("[hidden]"));
    if (item) this._openItem(item);
  }

  _syncMobileLogo() {
    document.documentElement.classList.toggle("has-project-viewer-scroll", window.scrollY > 0);
  }

  // Blocks all hover/click interaction on the page while the scroll-into-view (or
  // scroll-restore) animation is running, so nothing is clickable mid-scroll.
  _setInteractionsBlocked(isBlocked) {
    document.body.classList.toggle("pointer-events-none", isBlocked);
  }

  // Shared by the zoom button and by clicking the image stream itself.
  _toggleZoom(panel) {
    const isZoomed = this._stream?.toggleZoom(panel);
    if (isZoomed === null || isZoomed === undefined) return;

    const btn = panel?.querySelector('[g-ref="zoom"]');
    if (btn) {
      btn.dataset.zoomed = isZoomed ? "true" : "false";
      btn.setAttribute("aria-label", isZoomed ? "Reset image height" : "Increase image height");
    }

    const stream = panel?.querySelector(".project-viewer-v2__item-images");
    stream?.classList.toggle("cursor-zoom-in", !isZoomed);
    stream?.classList.toggle("cursor-zoom-out", isZoomed);

    // Info and zoom are mutually exclusive: zooming in always closes the info panel.
    const item = panel?.previousElementSibling;
    if (item && isZoomed) this._setInfoPanelOpenForItem(item, false);
  }

  _activateTab(activeIndex) {
    this._closeItem(this._activeItem, { immediate: true });
    applyTabState(this.ref.btns_tab, this.ref.panel, activeIndex);

    // Headers are shared across tabs, so their aria-sort must reflect whichever
    // sort (if any) was last applied to the tab being switched to.
    const sortState = this._sortState.get(this.ref.panel[activeIndex]);
    syncSortHeaders(this.ref.sort, this._sortState, this.ref.panel[activeIndex]);
    if (sortState) {
      this.ref.sort.forEach((header) => {
        if (header.dataset.sortKey === sortState.key) {
          header.setAttribute("aria-sort", sortState.direction);
        }
      });
    }
  }

  _activePanel() {
    return this.ref.panel.find((panel) => !panel.hidden) ?? null;
  }

  _sortPanel(panel, key, activeHeader, forcedDirection) {
    const previousSort = this._sortState.get(panel);
    const direction = forcedDirection || getNextSortDirection(previousSort, key, "descending");
    applySort(panel, key, activeHeader, direction, this.ref.sort, this._sortState);
  }

  async _toggleItem(item) {
    if (this._activeItem === item) {
      await this._closeItem(item);
      return;
    }

    if (this._activeItem) await this._closeItem(this._activeItem);
    await this._openItem(item);
  }

  _openItem(item) {
    this._setActiveItem(item);
    this._resetPlayPauseButton(item);
    this._resetZoomButton(item);
    this._resetInfoPanel(item);
    this._scroller?.scrollToItem(item);
    return this._stream?.open(item);
  }

  _closeItem(item, options) {
    if (!item) return Promise.resolve();

    if (this._activeItem === item) this._setActiveItem(null);
    this._scroller?.restore();
    this._resetInfoPanel(item);
    return this._stream?.close(item, options);
  }

  // ProjectImageStream always resumes playing on open(), so the button must match.
  _resetPlayPauseButton(item) {
    const panel = item?.nextElementSibling;
    const btn = panel?.querySelector('[g-ref="playPause"]');
    if (!btn) return;

    btn.dataset.playing = "true";
    btn.setAttribute("aria-label", "Pause auto-scroll");
  }

  // ProjectImageStream always opens at the base height, so the button must match.
  _resetZoomButton(item) {
    const panel = item?.nextElementSibling;
    const btn = panel?.querySelector('[g-ref="zoom"]');

    const stream = panel?.querySelector(".project-viewer-v2__item-images");
    stream?.classList.add("cursor-zoom-in");
    stream?.classList.remove("cursor-zoom-out");

    if (!btn) return;

    btn.dataset.zoomed = "false";
    btn.setAttribute("aria-label", "Increase image height");
  }

  _infoPanelFor(item) {
    return infoPanelFor(item);
  }

  _toggleInfoPanel(item) {
    const panel = this._infoPanelFor(item);
    if (!panel) return;

    this._setInfoPanelOpenForItem(item, panel.classList.contains("hidden"));
  }

  // Shared by the info button's own click and the zoom button (zooming reveals info too).
  _setInfoPanelOpenForItem(item, isOpen) {
    setInfoPanelOpenForItem(item, isOpen);
  }

  _setInfoPanelOpen(panel, isOpen) {
    setInfoPanelOpen(panel, isOpen);
  }

  // Snaps the info panel shut (no animation) whenever the item itself opens/closes.
  _resetInfoPanel(item) {
    resetInfoPanel(item);
  }

  _setActiveItem(item) {
    this._activeItem = setActiveItem(item, this._activeItem);
  }
}
